import PropTypes from 'prop-types';
import { Button, Stack, Typography } from '@mui/material';

import { SORT_OPTIONS } from 'constants/common';
import { useBreakpointFlags } from 'hooks/useBreakpointFlags';
import { DownloadIcon } from 'assets/icons';

import { UsersDataGridTable } from './UsersDataGridTable';
import { MobileUsersCards } from './MobileUsersCards';

export const UsersDataGridList = ({
  tableTitle = '',
  currentPage,
  pageSize,
  rowCount,
  onPageChange,
  onPageSizeChange,
  columns,
  rows,
  mobileRows,
  isRowSelectable = () => true,
  isFetchingUsersList = false,
  order,
  orderBy,
  onTableSort,
  selectedRows = [],
  setSelectedRows = () => {},
  isDisabledSelection = false,
  usersBarButtons = [],
  onDownloadCsv = null,
  isDownloadingCsv = false,
  customActionStyles = null,
  hasMore = false,
  onLoadMore = () => {},
  isFilteredList = false,
  sortingMode = 'client',
  paginationMode = 'client',
  filterMode = 'client',
  filterModelItems = [],
  onFilterModelChange = () => {},
  itemsName,
  checkboxSelection = true,
}) => {
  const { isLgUp: isLgWidth } = useBreakpointFlags();

  return (
    <Stack spacing={{ xs: 4, md: 2 }} mb={{ xs: 4, lg: 8 }}>
      {tableTitle && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="subtitle1" component="p">
            {tableTitle}
          </Typography>

          {onDownloadCsv && (
            <Button
              size="small"
              color="info"
              loading={isDownloadingCsv}
              endIcon={<DownloadIcon />}
              onClick={onDownloadCsv}
            >
              Export Users CSV
            </Button>
          )}
        </Stack>
      )}

      {isLgWidth ? (
        <UsersDataGridTable
          currentPage={currentPage}
          pageSize={pageSize}
          rowCount={rowCount}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          columns={columns}
          rows={rows}
          isRowSelectable={isRowSelectable}
          isFetchingUsersList={isFetchingUsersList}
          order={order}
          orderBy={orderBy}
          onTableSort={onTableSort}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          isDisabledSelection={isDisabledSelection}
          usersBarButtons={usersBarButtons}
          sortingMode={sortingMode}
          paginationMode={paginationMode}
          filterMode={filterMode}
          filterModelItems={filterModelItems}
          onFilterModelChange={onFilterModelChange}
          isFilteredList={isFilteredList}
          itemsName={itemsName}
          checkboxSelection={checkboxSelection}
        />
      ) : (
        <MobileUsersCards
          users={mobileRows}
          customActionStyles={customActionStyles}
          isLoading={isFetchingUsersList}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          itemsName={itemsName}
        />
      )}
    </Stack>
  );
};

const barButtonPropTypes = {
  Icon: PropTypes.func,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

const actionPropTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.node,
  onClick: PropTypes.func,
  link: PropTypes.string,
  hideOption: PropTypes.bool,
};

const mobileInfoPropTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

UsersDataGridList.propTypes = {
  tableTitle: PropTypes.string,
  currentPage: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  rowCount: PropTypes.number.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isFetchingUsersList: PropTypes.bool,
  order: PropTypes.oneOf(Object.values(SORT_OPTIONS)).isRequired,
  orderBy: PropTypes.string.isRequired,
  selectedRows: PropTypes.arrayOf(PropTypes.string),
  isDisabledSelection: PropTypes.bool,
  usersBarButtons: PropTypes.arrayOf(
    PropTypes.shape({
      ...barButtonPropTypes,
      subItems: PropTypes.arrayOf(PropTypes.shape(barButtonPropTypes)),
    }),
  ),
  customActionStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  mobileRows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      isInactive: PropTypes.bool,
      actions: PropTypes.arrayOf(
        PropTypes.shape({
          ...actionPropTypes,
          subItems: PropTypes.arrayOf(PropTypes.shape(actionPropTypes)),
        }),
      ).isRequired,
      mobileInfo: PropTypes.shape({
        generalInfo: PropTypes.arrayOf(PropTypes.shape(mobileInfoPropTypes)),
        additionalInfo: PropTypes.arrayOf(PropTypes.shape(mobileInfoPropTypes)),
      }),
    }),
  ).isRequired,
  hasMore: PropTypes.bool,
  isFilteredList: PropTypes.bool,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  isRowSelectable: PropTypes.func,
  onTableSort: PropTypes.func.isRequired,
  setSelectedRows: PropTypes.func,
  onDownloadCsv: PropTypes.func,
  isDownloadingCsv: PropTypes.bool,
  onLoadMore: PropTypes.func,
  sortingMode: PropTypes.oneOf(['client', 'server']),
  paginationMode: PropTypes.oneOf(['client', 'server']),
  filterMode: PropTypes.oneOf(['client', 'server']),
  filterModelItems: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    }),
  ),
  onFilterModelChange: PropTypes.func,
  itemsName: PropTypes.string.isRequired,
  checkboxSelection: PropTypes.bool,
};
